// Administration › Entreprise : la fiche entreprise du mémoire technique, saisie une fois et réutilisée par chaque mémoire.
// Contrôlée par le serveur (règle KNX, coordonnées bancaires, secrets). Les attestations et certificats sont des pièces privées,
// jamais visibles dans la recherche. Une assurance échue est signalée et jamais citée comme valide dans un mémoire.
import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUp, Plus, Save, Trash2 } from 'lucide-react';
import { api, ErreurApi, messageErreur } from '../../lib/api.ts';
import { date } from '../../lib/format.ts';
import { Alerte, Badge, Bouton, Champ, ChampChoix, ChampTexte, Chargement, EtatErreur, Panneau, useNotifier } from '../../components/ui.tsx';

interface Piece { id: string; nature: string; titre: string; file_name: string; mime: string; size_bytes: number; cree_le: string }
interface Rubrique { id: string; titre: string; etat: 'vide' | 'partielle' | 'complete' }
interface VueFiche {
  fiche: Record<string, any>; version: number; modifie_le: string | null; pieces: Piece[];
  etat: { complete: boolean; rubriques: Rubrique[]; alertes: { code: string; gravite: string; message: string }[] };
  identite: { nom: string }; modifiable: boolean;
}

type TypeChamp = 'texte' | 'nombre' | 'date' | 'liste' | 'choix' | 'piece' | 'case';
interface DefChamp { cle: string; libelle: string; type?: TypeChamp; options?: Record<string, string>; large?: boolean; aide?: string }

const TYPES_ASSURANCE = { rc_pro: 'Responsabilité civile professionnelle', decennale: 'Responsabilité civile décennale', to_taches: 'Assurance complémentaire des travaux', autre: 'Autre assurance' };
const NATURES = { assurance: 'Attestation d’assurance', certification: 'Certificat ou qualification', reference: 'Pièce de référence', autre: 'Autre pièce' };
const ETATS: Record<Rubrique['etat'], { libelle: string; ton: 'succes' | 'ambre' | 'neutre' }> = {
  complete: { libelle: 'Complète', ton: 'succes' }, partielle: { libelle: 'Partielle', ton: 'ambre' }, vide: { libelle: 'Vide', ton: 'neutre' },
};

const LISTES: { cle: string; titre: string; aide: string; champs: DefChamp[]; vide: Record<string, unknown> }[] = [
  { cle: 'moyens_humains', titre: 'Moyens humains', aide: 'Équipes et qualifications : le mémoire en reprend la liste ; l’effectif affecté se saisit dans chaque mémoire.',
    champs: [{ cle: 'intitule', libelle: 'Fonction' }, { cle: 'nombre', libelle: 'Nombre', type: 'nombre' }, { cle: 'qualifications', libelle: 'Qualifications', type: 'liste', aide: 'Séparées par des virgules' },
      { cle: 'habilitations', libelle: 'Habilitations', type: 'liste', aide: 'Ex. : B2V, BR, BC' }],
    vide: { intitule: '', nombre: null, qualifications: [], habilitations: [] } },
  { cle: 'moyens_materiel', titre: 'Moyens matériels', aide: 'Outillage, appareils de mesure, valises de programmation, moyens d’accès, véhicules.',
    champs: [{ cle: 'intitule', libelle: 'Matériel' }, { cle: 'nombre', libelle: 'Nombre', type: 'nombre' }, { cle: 'annee', libelle: 'Année', type: 'nombre' }, { cle: 'usage', libelle: 'Usage' }],
    vide: { intitule: '', nombre: null, annee: null, usage: '' } },
  { cle: 'assurances', titre: 'Assurances', aide: 'Avec les dates : une attestation échue n’est jamais citée comme valide. La décennale est annoncée « à produire à l’attribution ».',
    champs: [{ cle: 'type', libelle: 'Type', type: 'choix', options: TYPES_ASSURANCE }, { cle: 'assureur', libelle: 'Assureur' }, { cle: 'police', libelle: 'N° de police' },
      { cle: 'valide_du', libelle: 'Valable du', type: 'date' }, { cle: 'valide_au', libelle: 'au', type: 'date' }, { cle: 'activites_garanties', libelle: 'Activités garanties', large: true },
      { cle: 'piece_id', libelle: 'Attestation', type: 'piece' }],
    vide: { type: 'rc_pro', assureur: '', police: '', valide_du: null, valide_au: null, activites_garanties: '', piece_id: null } },
  { cle: 'certifications', titre: 'Qualifications et certifications', aide: 'Présentées comme des atouts, jamais comme une obligation légale.',
    champs: [{ cle: 'intitule', libelle: 'Intitulé' }, { cle: 'organisme', libelle: 'Organisme' }, { cle: 'numero', libelle: 'Numéro' }, { cle: 'valide_au', libelle: 'Valable jusqu’au', type: 'date' },
      { cle: 'piece_id', libelle: 'Certificat', type: 'piece' }],
    vide: { intitule: '', organisme: '', numero: '', valide_au: null, piece_id: null } },
  { cle: 'references', titre: 'Références de chantiers', aide: '3 à 5 chantiers comparables. Le contact n’est imprimé que si vous l’autorisez.',
    champs: [{ cle: 'operation', libelle: 'Opération' }, { cle: 'maitre_ouvrage', libelle: 'Maître d’ouvrage' }, { cle: 'maitre_oeuvre', libelle: 'Maître d’œuvre' },
      { cle: 'annee', libelle: 'Année', type: 'nombre' }, { cle: 'montant_ht', libelle: 'Montant HT (€)', type: 'nombre' }, { cle: 'description', libelle: 'Description', large: true },
      { cle: 'contact', libelle: 'Contact' }, { cle: 'diffuser_contact', libelle: 'Imprimer le contact', type: 'case' }],
    vide: { operation: '', maitre_ouvrage: '', maitre_oeuvre: '', annee: null, montant_ht: null, description: '', contact: '', diffuser_contact: false, piece_id: null } },
  { cle: 'fournisseurs_habituels', titre: 'Fournisseurs habituels', aide: 'Marques et distributeurs cités dans la provenance des matériaux.',
    champs: [{ cle: 'famille', libelle: 'Famille' }, { cle: 'marque', libelle: 'Marque' }, { cle: 'distributeur', libelle: 'Distributeur' }],
    vide: { famille: '', marque: '', distributeur: '' } },
];

const TEXTES: { cle: string; titre: string; champs: DefChamp[] }[] = [
  { cle: 'securite', titre: 'Hygiène et sécurité', champs: [{ cle: 'encadrement', libelle: 'Encadrement de la prévention' }, { cle: 'accueil_nouveaux', libelle: 'Accueil sécurité des nouveaux arrivants' },
    { cle: 'epi', libelle: 'Équipements de protection individuelle', type: 'liste' }, { cle: 'analyse_risques_texte', libelle: 'Démarche d’analyse des risques' }, { cle: 'sous_traitance', libelle: 'Sous-traitance habituelle' }] },
  { cle: 'environnement', titre: 'Environnement', champs: [{ cle: 'tri', libelle: 'Tri des déchets' }, { cle: 'nuisances', libelle: 'Limitation des nuisances' }, { cle: 'protection_existant', libelle: 'Protection de l’existant' }, { cle: 'charte', libelle: 'Charte ou engagements' }] },
  { cle: 'qualite', titre: 'Qualité, essais et DOE', champs: [{ cle: 'autocontroles', libelle: 'Autocontrôles' }, { cle: 'fiches_de_controle', libelle: 'Fiches de contrôle' }, { cle: 'essais_recette', libelle: 'Essais et recette' }, { cle: 'doe', libelle: 'Dossier des ouvrages exécutés' }] },
];

export function OngletEntreprise() {
  const q = useQuery({ queryKey: ['fiche-entreprise'], queryFn: () => api.get<VueFiche>('/api/organisation/fiche') });
  if (q.isLoading) return <Chargement lignes={6} />;
  if (q.isError || !q.data) return <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />;
  return <EditeurFiche key={q.data.version} vue={q.data} />;
}

function EditeurFiche({ vue }: { vue: VueFiche }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [f, setF] = useState<Record<string, any>>(() => structuredClone(vue.fiche));
  const [modifie, setModifie] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreurs, setErreurs] = useState<{ champ?: string; message: string }[]>([]);
  const ro = !vue.modifiable;
  const maj = (fn: (x: Record<string, any>) => void) => { setF((avant) => { const x = structuredClone(avant); fn(x); return x; }); setModifie(true); };

  const enregistrer = async () => {
    setEnCours(true);
    try {
      await api.put('/api/organisation/fiche', { fiche: f, version: vue.version });
      setModifie(false); setErreurs([]);
      await qc.invalidateQueries({ queryKey: ['fiche-entreprise'] });
      notifier('Fiche entreprise enregistrée. Les prochains mémoires la reprendront.');
    } catch (e) {
      if (e instanceof ErreurApi && Array.isArray(e.details)) setErreurs(e.details as any);
      notifier(messageErreur(e), true);
    } finally { setEnCours(false); }
  };

  const id = f.identite ?? {}, org = f.organisation ?? {};
  return (
    <div className="me-fiche pile-l">
      <Panneau titre={`Fiche entreprise — ${vue.identite.nom}`}
        actions={!ro ? <Bouton variante="principal" icone={<Save aria-hidden />} chargement={enCours} disabled={!modifie} onClick={enregistrer}>Enregistrer</Bouton> : undefined}>
        <div className="pile">
          <p className="petit secondaire">Saisie une fois, reprise par chaque mémoire technique. Rien n’y est deviné : une rubrique vide apparaît « à compléter » dans le mémoire.
            {vue.modifie_le && ` Dernière modification le ${date(vue.modifie_le)}.`}</p>
          <ul className="me-rubriques">
            {vue.etat.rubriques.map((r) => <li key={r.id}><Badge ton={ETATS[r.etat].ton} icone={false}>{ETATS[r.etat].libelle}</Badge> <span className="petit">{r.titre}</span></li>)}
          </ul>
          {vue.etat.alertes.length > 0 && (
            <Alerte ton="ambre" titre="À vérifier"><ul className="et-liste-simple">{vue.etat.alertes.map((a, i) => <li key={i}>{a.message}</li>)}</ul></Alerte>
          )}
          {erreurs.length > 0 && <Alerte ton="danger" titre="Enregistrement refusé"><ul className="et-liste-simple">{erreurs.map((e, i) => <li key={i}>{e.message}</li>)}</ul></Alerte>}
          {modifie && !ro && <Alerte ton="info">Modifications non enregistrées.</Alerte>}
        </div>
      </Panneau>

      <Panneau titre="Identité">
        <div className="grille-form">
          <Champ libelle="Année de création" type="number" inputMode="numeric" readOnly={ro} value={id.annee_creation ?? ''} onChange={(e) => maj((x) => { x.identite.annee_creation = e.target.value ? Number(e.target.value) : null; })} />
          <Champ libelle="Effectif total" type="number" inputMode="numeric" readOnly={ro} value={id.effectif_total ?? ''} onChange={(e) => maj((x) => { x.identite.effectif_total = e.target.value ? Number(e.target.value) : null; })} />
          <Champ libelle="Zone d’intervention" readOnly={ro} value={id.zone_intervention ?? ''} onChange={(e) => maj((x) => { x.identite.zone_intervention = e.target.value; })} />
          <Champ libelle="Couleur du document" aide="Code couleur, ex. #006367" readOnly={ro} value={id.couleur ?? ''} onChange={(e) => maj((x) => { x.identite.couleur = e.target.value || null; })} />
        </div>
        <div style={{ marginTop: 'var(--e-4)' }}>
          <ChampTexte libelle="Métiers exercés" rows={2} readOnly={ro} value={id.metiers ?? ''} onChange={(e) => maj((x) => { x.identite.metiers = e.target.value; })} />
        </div>
      </Panneau>

      <Panneau titre="Organisation">
        <div className="pile">
          <ChampTexte libelle="Organisation et chaîne de décision" rows={3} readOnly={ro} value={org.organigramme_texte ?? ''} onChange={(e) => maj((x) => { x.organisation.organigramme_texte = e.target.value; })} />
          <ChampTexte libelle="Réunions et suivi" rows={2} readOnly={ro} value={org.reunions ?? ''} onChange={(e) => maj((x) => { x.organisation.reunions = e.target.value; })} />
          <EditeurListe titre="Fonctions" aide="Le nom d’une personne n’est imprimé que si vous la retenez pour le chantier." ro={ro} pieces={vue.pieces}
            lignes={org.fonctions ?? []} champs={[{ cle: 'role', libelle: 'Fonction' }, { cle: 'nom', libelle: 'Nom' }, { cle: 'qualification', libelle: 'Qualification' }, { cle: 'anciennete', libelle: 'Ancienneté' }]}
            vide={{ role: '', nom: '', qualification: '', anciennete: '' }} changer={(l) => maj((x) => { x.organisation.fonctions = l; })} />
        </div>
      </Panneau>

      {LISTES.map((l) => (
        <Panneau key={l.cle} titre={l.titre}>
          <EditeurListe titre={l.titre} aide={l.aide} ro={ro} pieces={vue.pieces} lignes={f[l.cle] ?? []} champs={l.champs} vide={l.vide}
            changer={(v) => maj((x) => { x[l.cle] = v; })} />
        </Panneau>
      ))}

      {TEXTES.map((t) => (
        <Panneau key={t.cle} titre={t.titre}>
          <div className="pile">
            {t.champs.map((c) => c.type === 'liste'
              ? <Champ key={c.cle} libelle={c.libelle} aide="Séparés par des virgules" readOnly={ro} value={(f[t.cle]?.[c.cle] ?? []).join(', ')}
                  onChange={(e) => maj((x) => { x[t.cle][c.cle] = e.target.value.split(',').map((s) => s.trim()).filter(Boolean); })} />
              : <ChampTexte key={c.cle} libelle={c.libelle} rows={2} readOnly={ro} value={f[t.cle]?.[c.cle] ?? ''} onChange={(e) => maj((x) => { x[t.cle][c.cle] = e.target.value; })} />)}
          </div>
        </Panneau>
      ))}

      <PiecesFiche pieces={vue.pieces} ro={ro} />
      {!ro && modifie && (
        <div className="me-generer"><Bouton variante="principal" icone={<Save aria-hidden />} chargement={enCours} onClick={enregistrer}>Enregistrer la fiche</Bouton></div>
      )}
    </div>
  );
}

function EditeurListe({ titre, aide, lignes, champs, vide, changer, ro, pieces }:
  { titre: string; aide: string; lignes: Record<string, any>[]; champs: DefChamp[]; vide: Record<string, unknown>; changer: (l: Record<string, any>[]) => void; ro: boolean; pieces: Piece[] }) {
  const majLigne = (i: number, cle: string, v: unknown) => changer(lignes.map((l, k) => (k === i ? { ...l, [cle]: v } : l)));
  return (
    <div className="pile">
      <p className="petit secondaire">{aide}</p>
      {lignes.length === 0 && <p className="secondaire petit">Aucune ligne.</p>}
      <ol className="me-lignes">
        {lignes.map((l, i) => (
          <li key={i} className="me-ligne">
            <div className="grille-form">
              {champs.map((c) => {
                const v = l[c.cle];
                if (c.type === 'choix') return <ChampChoix key={c.cle} libelle={c.libelle} disabled={ro} value={v ?? ''} options={Object.entries(c.options ?? {}).map(([valeur, libelle]) => ({ valeur, libelle }))} onChange={(e) => majLigne(i, c.cle, e.target.value)} />;
                if (c.type === 'piece') return <ChampChoix key={c.cle} libelle={c.libelle} disabled={ro} value={v ?? ''} vide="Aucune pièce jointe" options={pieces.map((p) => ({ valeur: p.id, libelle: p.titre }))} onChange={(e) => majLigne(i, c.cle, e.target.value || null)} />;
                if (c.type === 'case') return <label key={c.cle} className="case"><input type="checkbox" disabled={ro} checked={!!v} onChange={(e) => majLigne(i, c.cle, e.target.checked)} /><span className="petit">{c.libelle}</span></label>;
                if (c.type === 'liste') return <Champ key={c.cle} libelle={c.libelle} aide={c.aide} readOnly={ro} value={(v ?? []).join(', ')} onChange={(e) => majLigne(i, c.cle, e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />;
                return <Champ key={c.cle} libelle={c.libelle} readOnly={ro} className={c.large ? 'me-champ-large' : undefined}
                  type={c.type === 'nombre' ? 'number' : c.type === 'date' ? 'date' : 'text'} inputMode={c.type === 'nombre' ? 'decimal' : undefined}
                  value={v ?? ''} onChange={(e) => majLigne(i, c.cle, c.type === 'nombre' ? (e.target.value === '' ? null : Number(e.target.value)) : c.type === 'date' ? (e.target.value || null) : e.target.value)} />;
              })}
            </div>
            {!ro && <Bouton compact variante="fantome" icone={<Trash2 aria-hidden />} onClick={() => changer(lignes.filter((_, k) => k !== i))}>Retirer</Bouton>}
          </li>
        ))}
      </ol>
      {!ro && <div><Bouton compact icone={<Plus aria-hidden />} onClick={() => changer([...lignes, structuredClone(vide)])}>Ajouter — {titre.toLowerCase()}</Bouton></div>}
    </div>
  );
}

function PiecesFiche({ pieces, ro }: { pieces: Piece[]; ro: boolean }) {
  const qc = useQueryClient();
  const notifier = useNotifier();
  const [nature, setNature] = useState('assurance');
  const [titre, setTitre] = useState('');
  const [enCours, setEnCours] = useState(false);
  const fichier = useRef<HTMLInputElement>(null);
  const deposer = async () => {
    const fic = fichier.current?.files?.[0];
    if (!fic || !titre.trim()) { notifier('Choisissez un fichier et donnez-lui un titre.', true); return; }
    setEnCours(true);
    try {
      const fd = new FormData();
      fd.append('meta', JSON.stringify({ nature, titre: titre.trim() }));
      fd.append('fichier', fic);
      await api.envoyer('/api/organisation/pieces', fd);
      setTitre(''); if (fichier.current) fichier.current.value = '';
      await qc.invalidateQueries({ queryKey: ['fiche-entreprise'] });
      notifier('Pièce déposée. Rattachez-la à l’assurance ou au certificat concerné, puis enregistrez la fiche.');
    } catch (e) { notifier(messageErreur(e), true); } finally { setEnCours(false); }
  };
  const retirer = async (p: Piece) => {
    try { await api.del(`/api/organisation/pieces/${p.id}`); await qc.invalidateQueries({ queryKey: ['fiche-entreprise'] }); notifier('Pièce retirée.'); }
    catch (e) { notifier(messageErreur(e), true); }
  };
  return (
    <Panneau titre="Pièces de la fiche (attestations, certificats)">
      <div className="pile">
        <p className="petit secondaire">Fichiers privés : jamais visibles dans la recherche, consultables par les administrateurs et les chargés d’affaires. PDF, PNG ou JPEG.</p>
        {pieces.length === 0 ? <p className="secondaire petit">Aucune pièce.</p> : (
          <ul className="me-pieces">
            {pieces.map((p) => (
              <li key={p.id}>
                <a href={`/api/organisation/pieces/${p.id}/fichier`} target="_blank" rel="noopener">{p.titre}</a>
                <span className="petit secondaire"> — {NATURES[p.nature as keyof typeof NATURES] ?? p.nature}, déposée le {date(p.cree_le)}</span>
                {!ro && <Bouton compact variante="fantome" icone={<Trash2 aria-hidden />} onClick={() => retirer(p)}>Retirer</Bouton>}
              </li>
            ))}
          </ul>
        )}
        {!ro && (
          <div className="grille-form">
            <ChampChoix libelle="Nature" value={nature} options={Object.entries(NATURES).map(([valeur, libelle]) => ({ valeur, libelle }))} onChange={(e) => setNature(e.target.value)} />
            <Champ libelle="Titre" value={titre} onChange={(e) => setTitre(e.target.value)} />
            <div className="champ"><label className="champ-libelle" htmlFor="me-fichier">Fichier</label>
              <input id="me-fichier" ref={fichier} type="file" accept="application/pdf,image/png,image/jpeg" className="saisie" /></div>
            <div className="me-depot"><Bouton icone={<FileUp aria-hidden />} chargement={enCours} onClick={deposer}>Déposer la pièce</Bouton></div>
          </div>
        )}
      </div>
    </Panneau>
  );
}
